import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CleanPlace from '../assets/Images/CleanPlace.jpg';
import {
  UilPhone, UilEnvelope, UilFacebookF, UilInstagramAlt,
  UilClock, UilMapMarker, UilCalendarAlt, UilCheckCircle, UilStar,
} from '@iconscout/react-unicons';
import Request from '../components/Request';
import FooterPage from '../components/FooterPage';

const HOURS = [
  { day: 'Monday – Friday', hours: '7:00 AM – 6:00 PM', open: true },
  { day: 'Saturday',        hours: '7:00 AM – 1:00 PM', open: true },
  { day: 'Sunday',          hours: 'Closed',             open: false },
];

const REASONS = [
  { icon: <UilStar size={28} />,        title: 'Fast Response',      description: 'We respond to all inquiries within 2 hours',  color: 'bg-amber-50 text-amber-600' },
  { icon: <UilCheckCircle size={28} />, title: 'Free Quotes',        description: 'No-obligation estimates for all services',      color: 'bg-brand-50 text-brand-600' },
  { icon: <UilCalendarAlt size={28} />, title: 'Flexible Scheduling', description: 'Book at your convenience',                    color: 'bg-violet-50 text-violet-600' },
];

export default function Contact() {
  return (
    <div className="bg-white text-slate-900">
      <Helmet>
        <title>Contact Us - Fabiana Fabulous Cleaning Services | (425) 829-6092</title>
        <meta name="description" content="Contact Fabiana Fabulous Cleaning Services for a free quote. Call (425) 829-6092 or email fabulous@fabycleaners.com. Mon-Fri 7am-6pm, Sat 7am-1pm." />
        <link rel="canonical" href="https://fabycleaners.com/contact" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-end bg-cover bg-center" style={{ backgroundImage: `url(${CleanPlace})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/20" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center text-white">
            <span className="inline-block bg-gold-500/20 border border-gold-400/30 text-gold-300 text-xs font-semibold px-4 py-2 rounded-full mb-5">We're Here to Help</span>
            <h1 className="text-5xl md:text-6xl font-bold mb-5 leading-tight">Get In Touch</h1>
            <p className="text-white/65 text-xl max-w-xl mx-auto mb-10">Have questions? We're ready to help with all your cleaning needs.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:425-829-6092"
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold px-7 py-3.5 rounded-xl transition-colors">
                <UilPhone size={18} /> (425) 829-6092
              </a>
              <a href="mailto:fabulous@fabycleaners.com"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold px-7 py-3.5 rounded-xl transition-all">
                <UilEnvelope size={18} /> Send Email
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Reach Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Contact Methods</h2>
            <p className="text-slate-500">Choose the best way to reach us.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              { title: 'Call Us',    detail: '(425) 829-6092',              link: 'tel:425-829-6092',                    desc: 'Mon–Fri 7am–6pm, Sat 7am–1pm', icon: <UilPhone size={32} />,   color: 'bg-brand-50 text-brand-600' },
              { title: 'Email Us',   detail: 'fabulous@fabycleaners.com',   link: 'mailto:fabulous@fabycleaners.com',    desc: 'We reply within 24 hours',     icon: <UilEnvelope size={32} />, color: 'bg-emerald-50 text-emerald-600' },
            ].map((m, idx) => (
              <motion.a key={m.title} href={m.link}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -4 }} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-card hover:shadow-card-hover transition-all flex items-start gap-6">
                <div className={`w-16 h-16 ${m.color} rounded-2xl flex items-center justify-center shrink-0`}>{m.icon}</div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-1">{m.title}</h3>
                  <p className="text-brand-600 font-bold text-xl mb-1">{m.detail}</p>
                  <p className="text-sm text-slate-500">{m.desc}</p>
                </div>
              </motion.a>
            ))}
          </div>

          {/* Social */}
          <div className="text-center">
            <h3 className="font-bold text-slate-900 mb-5">Follow Us</h3>
            <div className="flex justify-center gap-3">
              {[
                { href: 'https://www.facebook.com/Fabulouscleaner', icon: <UilFacebookF size={20} />, label: 'Facebook',  color: 'hover:bg-blue-600' },
                { href: 'https://www.instagram.com/fabianafabulouscleaningservice/', icon: <UilInstagramAlt size={20} />, label: 'Instagram', color: 'hover:bg-pink-500' },
              ].map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  className={`w-12 h-12 bg-slate-100 ${s.color} hover:text-white rounded-xl flex items-center justify-center text-slate-600 transition-all duration-200`}>
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Hours & Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8">

            {/* Hours */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-brand-50 rounded-2xl flex items-center justify-center text-brand-600">
                  <UilClock size={24} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Business Hours</h2>
              </div>
              <div className="space-y-3">
                {HOURS.map((s) => (
                  <div key={s.day} className="flex justify-between items-center py-3 border-b border-slate-200 last:border-0">
                    <span className="font-medium text-slate-700">{s.day}</span>
                    <span className={`text-sm font-semibold px-3 py-1 rounded-lg ${s.open ? 'bg-brand-50 text-brand-700' : 'bg-slate-200 text-slate-500'}`}>
                      {s.hours}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Areas */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-brand-50 rounded-2xl flex items-center justify-center text-brand-600">
                  <UilMapMarker size={24} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Service Areas</h2>
              </div>
              <p className="text-slate-600 mb-6 leading-relaxed">We proudly serve Seattle, Bellevue, Redmond, and surrounding areas in the Eastside.</p>
              <Link to="/about">
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3.5 rounded-xl transition-colors text-sm">
                  View All Service Areas
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Contact */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Easy to Start</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Why Contact Us?</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {REASONS.map((r, idx) => (
              <motion.div key={r.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-card text-center">
                <div className={`w-16 h-16 ${r.color} rounded-2xl flex items-center justify-center mx-auto mb-5`}>{r.icon}</div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{r.title}</h3>
                <p className="text-slate-500 text-sm">{r.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Request />
      <FooterPage />
    </div>
  );
}
